import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { Alert, DialogActions, DialogContent, Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import { GET_MY_AMCR_ACCOUNT_QUERY, SIGN_IN_AMCR_MUTATION } from '../../gql';
import { emailRegex, useYupValidationResolver } from '../../helpers';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceTextField,
} from '../design-system';

interface AmcrSignInFormProps {
  isSessionExpired?: boolean;
  onCancel: () => void;
  onSignedIn: () => void;
}

interface AmcrSignInValues {
  username: string;
  password: string;
}

const contentStyles = { minWidth: { sm: 420 } };
const fieldsStyles = { gap: 2 };
const errorListStyles = { m: 0, pl: 2 };

const toLowerCase = (value: string) => value.trim().toLowerCase();

const AmcrSignInForm = ({
  isSessionExpired = false,
  onCancel,
  onSignedIn,
}: AmcrSignInFormProps) => {
  const intl = useIntl();
  const [errors, setErrors] = useState<Array<{ error: string }>>([]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        username: yup
          .string()
          .required(
            intl.formatMessage({
              id: 'amcrPas.signIn.usernameRequired',
              defaultMessage: 'Enter your AMCR-PAS e-mail.',
            })
          )
          .matches(
            emailRegex,
            intl.formatMessage({
              id: 'amcrPas.signIn.usernameInvalid',
              defaultMessage: 'Enter a valid e-mail.',
            })
          ),
        password: yup.string().required(
          intl.formatMessage({
            id: 'amcrPas.signIn.passwordRequired',
            defaultMessage: 'Enter your AMCR-PAS password.',
          })
        ),
      }),
    [intl]
  );
  const resolver = useYupValidationResolver(validationSchema, setErrors);
  const { register, handleSubmit } = useForm<AmcrSignInValues>({ resolver });

  const [signInAmcr, { loading }] = useMutation(SIGN_IN_AMCR_MUTATION, {
    update: (cache, { data }) => {
      const account = data?.signInAmcr?.account;

      if (account) {
        cache.writeQuery({
          query: GET_MY_AMCR_ACCOUNT_QUERY,
          data: { getMyAmcrAccount: account },
        });
      }
    },
  });

  const handleSignIn = async (values: AmcrSignInValues) => {
    setErrors([]);

    try {
      const { data } = await signInAmcr({ variables: values });
      const result = data?.signInAmcr;

      if (result?.account) {
        onSignedIn();
        return;
      }

      setErrors(result?.errors || []);
    } catch {
      setErrors([
        {
          error: intl.formatMessage({
            id: 'amcrPas.signIn.failed',
            defaultMessage: 'Signing in to AMCR-PAS failed. Please try again.',
          }),
        },
      ]);
    }
  };

  return (
    <Stack component="form" noValidate onSubmit={handleSubmit(handleSignIn)}>
      <DialogContent sx={contentStyles}>
        <Stack sx={fieldsStyles}>
          {isSessionExpired && (
            <Alert severity="warning" variant="outlined">
              <FormattedMessage
                id="amcrPas.signIn.expired"
                defaultMessage="Your AMCR-PAS sign-in has expired. Sign in again to continue."
              />
            </Alert>
          )}
          <Alert severity="info" variant="outlined">
            <FormattedMessage
              id="amcrPas.signIn.credentialsNotice"
              defaultMessage="Acolyte does not store your AMCR-PAS credentials. Only an access token for working with AMCR-PAS is kept after you sign in."
            />
          </Alert>
          <FormField>
            <FieldLabel htmlFor="amcr-username">
              <FormattedMessage
                id="amcrPas.signIn.username"
                defaultMessage="AMCR-PAS e-mail"
              />
            </FieldLabel>
            <SurfaceTextField
              fullWidth
              autoFocus
              id="amcr-username"
              type="email"
              autoComplete="username"
              surface="sunken"
              {...register('username', { setValueAs: toLowerCase })}
            />
          </FormField>
          <FormField>
            <FieldLabel htmlFor="amcr-password">
              <FormattedMessage
                id="amcrPas.signIn.password"
                defaultMessage="AMCR-PAS password"
              />
            </FieldLabel>
            <SurfaceTextField
              fullWidth
              id="amcr-password"
              type="password"
              autoComplete="current-password"
              surface="sunken"
              {...register('password')}
            />
          </FormField>
          {errors.length > 0 && (
            <Alert severity="error" variant="outlined">
              <Stack component="ul" sx={errorListStyles}>
                {errors.map(({ error }) => (
                  <li key={error}>{error}</li>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button tone="neutral" disabled={loading} onClick={onCancel}>
          <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="submit" disabled={loading}>
          <FormattedMessage
            id="amcrPas.signIn.submit"
            defaultMessage="Sign in"
          />
        </Button>
      </DialogActions>
    </Stack>
  );
};

export { AmcrSignInForm };
